import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { httpClient } from "../../dependencies/utils/Api";
import { UPLOAD_SERVICE_IMAGE } from "../../constants/ApplicationUrl";
import { getDocumentLink } from "../../dependencies/utils/helper";

function UploadImageModal({ show, onClose, service, onSuccess }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/avif"];
    if (!allowed.includes(f.type)) {
      toast.error("Allowed formats: JPG, PNG, WEBP, AVIF.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10 MB).");
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please choose a file first.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const url = `${UPLOAD_SERVICE_IMAGE}/${service._id}/upload-image`;
      const { data: res } = await httpClient.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res?.status) {
        toast.success("Image uploaded successfully.");
        onSuccess?.(res.data?.image);
        handleClose();
      } else {
        toast.error(res?.message || "Upload failed.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setUploading(false);
    onClose?.();
  };

  if (!service) return null;
  const currentImage = service.image ? getDocumentLink(service.image) : null;

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18 }}>
          <i className="fas fa-image" style={{ marginRight: 8, color: "#1263AC" }}></i>
          Upload Image — {service.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-md-6">
            <label className="text-muted" style={{ fontSize: 13, fontWeight: 600 }}>
              Current
            </label>
            <div
              style={{
                border: "1px solid #eef2f7",
                borderRadius: 8,
                height: 180,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#f6f9fc",
                overflow: "hidden",
              }}
            >
              {currentImage ? (
                <img
                  src={currentImage}
                  alt="current"
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <span className="text-muted">No image</span>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <label className="text-muted" style={{ fontSize: 13, fontWeight: 600 }}>
              New (preview)
            </label>
            <div
              style={{
                border: preview ? "2px solid #28a745" : "2px dashed #ccc",
                borderRadius: 8,
                height: 180,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: preview ? "#fff" : "#fafbfc",
                overflow: "hidden",
              }}
            >
              {preview ? (
                <img
                  src={preview}
                  alt="preview"
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                />
              ) : (
                <span className="text-muted" style={{ fontSize: 13 }}>
                  Pick a file to preview
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <input
            type="file"
            accept="image/png, image/jpeg, image/webp, image/avif"
            onChange={handleFileChange}
            className="form-control"
            disabled={uploading}
          />
          <small className="text-muted">JPG / PNG / WEBP / AVIF · max 10 MB · stored on Supabase.</small>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleClose}
          disabled={uploading}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleUpload}
          disabled={!file || uploading}
        >
          {uploading ? (
            <>
              <span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }}></span>
              Uploading…
            </>
          ) : (
            <>
              <i className="fas fa-cloud-upload-alt" style={{ marginRight: 6 }}></i>
              Upload to Supabase
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default UploadImageModal;
